import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React__default, { useContext, useState, useRef, useCallback, useEffect, cloneElement } from 'react';
import * as FeatureFlags from '@carbon/feature-flags';
import ReactDOM from 'react-dom';
import window from 'window-or-global';
import { Tab } from './keyboard/keys.js';
import { match } from './keyboard/match.js';
import { selectorTabbable, selectorFocusable } from './keyboard/navigation.js';
import { OptimizedResize } from './OptimizedResize.js';
import { PrefixContext } from './usePrefix.js';
import { warning } from './warning.js';
import wrapFocus, { wrapFocusWithoutSentinels } from './wrapFocus.js';
var DIRECTION_LEFT = 'left';
var DIRECTION_TOP = 'top';
var DIRECTION_RIGHT = 'right';
var DIRECTION_BOTTOM = 'bottom';
/**
 * Computes the floating menu's position based on the menu size, trigger element
 * position, offset, direction, and container.
 */
var getFloatingPosition = function getFloatingPosition(_ref) {
  var _positions;
  var menuSize = _ref.menuSize,
    refPosition = _ref.refPosition,
    offset = _ref.offset,
    direction = _ref.direction,
    scrollX = _ref.scrollX,
    scrollY = _ref.scrollY,
    container = _ref.container;
  var _refPosition$left = refPosition.left,
    refLeft = _refPosition$left === void 0 ? 0 : _refPosition$left,
    _refPosition$top = refPosition.top,
    refTop = _refPosition$top === void 0 ? 0 : _refPosition$top,
    _refPosition$right = refPosition.right,
    refRight = _refPosition$right === void 0 ? 0 : _refPosition$right,
    _refPosition$bottom = refPosition.bottom,
    refBottom = _refPosition$bottom === void 0 ? 0 : _refPosition$bottom;
  var effectiveScrollX = container.position !== 'static' ? 0 : scrollX;
  var effectiveScrollY = container.position !== 'static' ? 0 : scrollY;
  var relativeDiff = {
    top: container.position !== 'static' ? container.rect.top : 0,
    left: container.position !== 'static' ? container.rect.left : 0
  };
  var width = menuSize.width,
    height = menuSize.height;
  var _offset$top = offset.top,
    top = _offset$top === void 0 ? 0 : _offset$top,
    _offset$left = offset.left,
    left = _offset$left === void 0 ? 0 : _offset$left;
  var refCenterHorizontal = (refLeft + refRight) / 2;
  var refCenterVertical = (refTop + refBottom) / 2;
  var positions = (_positions = {}, _defineProperty(_positions, DIRECTION_LEFT, function () {
    return {
      left: refLeft - width + effectiveScrollX - left - relativeDiff.left,
      top: refCenterVertical - height / 2 + effectiveScrollY + top - 9 - relativeDiff.top
    };
  }), _defineProperty(_positions, DIRECTION_TOP, function () {
    return {
      left: refCenterHorizontal - width / 2 + effectiveScrollX + left - relativeDiff.left,
      top: refTop - height + effectiveScrollY - top - relativeDiff.top
    };
  }), _defineProperty(_positions, DIRECTION_RIGHT, function () {
    return {
      left: refRight + effectiveScrollX + left - relativeDiff.left,
      top: refCenterVertical - height / 2 + effectiveScrollY + top + 3 - relativeDiff.top
    };
  }), _defineProperty(_positions, DIRECTION_BOTTOM, function () {
    return {
      left: refCenterHorizontal - width / 2 + effectiveScrollX + left - relativeDiff.left,
      top: refBottom + effectiveScrollY + top - relativeDiff.top
    };
  }), _positions);
  return positions[direction]();
};
var FloatingMenu = function FloatingMenu(_ref2) {
  var children = _ref2.children,
    flipped = _ref2.flipped,
    focusTrap = _ref2.focusTrap,
    _ref2$menuDirection = _ref2.menuDirection,
    menuDirection = _ref2$menuDirection === void 0 ? DIRECTION_BOTTOM : _ref2$menuDirection,
    _ref2$menuOffset = _ref2.menuOffset,
    menuOffset = _ref2$menuOffset === void 0 ? {
      top: 0,
      left: 0
    } : _ref2$menuOffset,
    externalMenuRef = _ref2.menuRef,
    onPlace = _ref2.onPlace,
    selectorPrimaryFocus = _ref2.selectorPrimaryFocus,
    styles = _ref2.styles,
    _ref2$target = _ref2.target,
    target = _ref2$target === void 0 ? function () {
      return document.body;
    } : _ref2$target,
    triggerRef = _ref2.triggerRef,
    updateOrientation = _ref2.updateOrientation;
  var prefix = useContext(PrefixContext);
  var _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    floatingPosition = _useState2[0],
    setFloatingPosition = _useState2[1];
  var menuBodyRef = useRef(null);
  var startSentinelRef = useRef(null);
  var endSentinelRef = useRef(null);
  var placeInProgressRef = useRef(false);
  var updateMenuPosition = useCallback(function (isAdjustment) {
    var menuBody = menuBodyRef.current;
    if (!menuBody) {
      process.env.NODE_ENV !== "production" ? warning(!!menuBody, 'The DOM node for menu body for calculating its position is not available. Skipping...') : void 0;
      return;
    }
    var triggerEl = triggerRef.current;
    var menuSize = menuBody.getBoundingClientRect();
    var refPosition = triggerEl ? triggerEl.getBoundingClientRect() : undefined;
    var offsetValue = typeof menuOffset === 'function' ? menuOffset(menuBody, menuDirection, triggerEl, flipped) : menuOffset;
    if (updateOrientation) {
      updateOrientation({
        menuSize: menuSize,
        refPosition: refPosition,
        direction: menuDirection,
        offset: offsetValue,
        scrollX: window.pageXOffset,
        scrollY: window.pageYOffset,
        container: {
          rect: target().getBoundingClientRect(),
          position: getComputedStyle(target()).position
        }
      });
    }

    // Only set position if the menu has a valid size or if no offset is provided.
    if (menuSize.width > 0 && menuSize.height > 0 || !offsetValue) {
      var newFloatingPosition = getFloatingPosition({
        menuSize: menuSize,
        refPosition: refPosition !== null && refPosition !== void 0 ? refPosition : {
          left: 0,
          top: 0,
          right: 0,
          bottom: 0
        },
        offset: offsetValue,
        direction: menuDirection,
        scrollX: window.pageXOffset,
        scrollY: window.pageYOffset,
        container: {
          rect: target().getBoundingClientRect(),
          position: getComputedStyle(target()).position
        }
      });

      // Only update if the position has actually changed.
      if (!floatingPosition || floatingPosition.left !== newFloatingPosition.left || floatingPosition.top !== newFloatingPosition.top) {
        setFloatingPosition(newFloatingPosition);
      }

      // Re-check after setting the position if not already adjusting.
      if (!isAdjustment) {
        var newMenuSize = menuBody.getBoundingClientRect();
        // TODO: Was there a bug in the old code? How could one `DOMRect` be
        // compared to another using `!==`?
        if (newMenuSize.width !== menuSize.width || newMenuSize.height !== menuSize.height) {
          updateMenuPosition(true);
        }
      }
    }
  }, [triggerRef, menuOffset, menuDirection, flipped, target, updateOrientation, floatingPosition]);
  var focusMenuContent = function focusMenuContent(menuBody) {
    var primaryFocusNode = selectorPrimaryFocus ? menuBody.querySelector(selectorPrimaryFocus) : null;
    var tabbableNode = menuBody.querySelector(selectorTabbable);
    var focusableNode = menuBody.querySelector(selectorFocusable);
    var focusTarget = primaryFocusNode ||
    // User defined focusable node
    tabbableNode ||
    // First sequentially focusable node
    focusableNode ||
    // First programmatic focusable node
    menuBody;
    focusTarget.focus();
    if (focusTarget === menuBody) {
      process.env.NODE_ENV !== "production" ? warning(focusableNode === null, 'Floating Menus must have at least a programmatically focusable child. This can be accomplished by adding tabIndex="-1" to the content element.') : void 0;
    }
  };
  var handleMenuRef = function handleMenuRef(node) {
    menuBodyRef.current = node;
    placeInProgressRef.current = !!node;
    if (externalMenuRef) {
      externalMenuRef(node);
    }
    if (node) {
      updateMenuPosition();
    }
  };

  // When the menu has been placed, focus the content and call onPlace.
  useEffect(function () {
    if (placeInProgressRef.current && floatingPosition && menuBodyRef.current) {
      if (!menuBodyRef.current.contains(document.activeElement)) {
        focusMenuContent(menuBodyRef.current);
      }
      if (typeof onPlace === 'function') {
        onPlace(menuBodyRef.current);
      }
      placeInProgressRef.current = false;
    }
  }, [floatingPosition, onPlace]);

  // Attach a resize listener.
  useEffect(function () {
    var resizeHandler = OptimizedResize.add(function () {
      updateMenuPosition();
    });
    return function () {
      resizeHandler.remove();
    };
  }, [triggerRef, menuOffset, menuDirection, flipped, target, updateOrientation]);

  // Update menu position when key props change.
  useEffect(function () {
    updateMenuPosition();
  }, [menuOffset, menuDirection, flipped, triggerRef, target, updateOrientation]);

  /**
   * Clones the child element to add a `ref` and positioning styles.
   */
  var getChildrenWithProps = function getChildrenWithProps() {
    var pos = floatingPosition;
    var positioningStyle = pos ? {
      left: "".concat(pos.left, "px"),
      top: "".concat(pos.top, "px"),
      right: 'auto'
    } : {
      visibility: 'hidden',
      top: '0px'
    };
    return /*#__PURE__*/cloneElement(children, {
      ref: handleMenuRef,
      style: _objectSpread(_objectSpread(_objectSpread({}, styles), positioningStyle), {}, {
        position: 'absolute',
        opacity: 1
      })
    });
  };

  /**
   * Blur handler used when focus trapping is enabled.
   */
  var handleBlur = function handleBlur(event) {
    if (menuBodyRef.current && startSentinelRef.current && endSentinelRef.current) {
      wrapFocus({
        bodyNode: menuBodyRef.current,
        startTrapNode: startSentinelRef.current,
        endTrapNode: endSentinelRef.current,
        currentActiveNode: event.relatedTarget,
        oldActiveNode: event.target
      });
    }
  };

  /**
   * Keydown handler for focus wrapping when experimental focus trap is enabled.
   */
  var handleKeyDown = function handleKeyDown(event) {
    if (match(event, Tab) && menuBodyRef.current) {
      wrapFocusWithoutSentinels({
        containerNode: menuBodyRef.current,
        currentActiveNode: event.target,
        event: event
      });
    }
  };
  var focusTrapWithoutSentinels = FeatureFlags.enabled('enable-experimental-focus-wrap-without-sentinels');
  if (typeof document !== 'undefined') {
    var portalTarget = target ? target() : document.body;
    return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React__default.createElement("div", {
      onBlur: focusTrap && !focusTrapWithoutSentinels ? handleBlur : undefined,
      onKeyDown: focusTrapWithoutSentinels ? handleKeyDown : undefined
    }, !focusTrapWithoutSentinels && /*#__PURE__*/React__default.createElement("span", {
      ref: startSentinelRef,
      tabIndex: 0,
      role: "link",
      className: "".concat(prefix, "--visually-hidden")
    }, "Focus sentinel"), getChildrenWithProps(), !focusTrapWithoutSentinels && /*#__PURE__*/React__default.createElement("span", {
      ref: endSentinelRef,
      tabIndex: 0,
      role: "link",
      className: "".concat(prefix, "--visually-hidden")
    }, "Focus sentinel")), portalTarget);
  }
  return null;
};
export { DIRECTION_BOTTOM, DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_TOP, FloatingMenu };